import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import { useRecordContext } from "react-admin";
import { DocumentRead, DocumentStatus } from "@/types";
import { rejectionReasonLabel } from "@/constants/documentRejection";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";

/**
 * Post-reject rail (#7441, artboard 1j): what the next reader of a rejected
 * document needs — that it was rejected, when, why, and that nothing loaded.
 *
 * Rendered on DocumentShow beside DocumentFailureBanner; self-hides for every
 * other status. The reason falls back to the raw stored text for documents
 * rejected before #7441 ("reason not provided"), which is still more honest
 * than hiding the line. The timestamp is modified_datetime: the status flip
 * to `rejected` is the last write a rejected document receives.
 */
export const DocumentRejectedBanner = () => {
  const documentRecord = useRecordContext<DocumentRead>();
  if (documentRecord?.status !== DocumentStatus.REJECTED) return null;

  const reason = rejectionReasonLabel(documentRecord.rejection_reason);
  const note = documentRecord.rejection_note?.trim();
  const when = documentRecord.modified_datetime
    ? formatDate(String(documentRecord.modified_datetime), DATE_FORMATS.DISPLAY_LONG_WITH_TIME)
    : null;

  return (
    <Box px={5} pb={1}>
      <Alert severity="error" sx={{ alignItems: "flex-start" }} data-testid="document-rejected-banner">
        <AlertTitle>{when ? `Rejected ${when}` : "Rejected"}</AlertTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2">Reason: {reason ?? "not recorded"}</Typography>
          {note && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              “{note}”
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Nothing was loaded. The uploader sees this reason.
          </Typography>
        </Box>
      </Alert>
    </Box>
  );
};
